import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface Supplier {
  id: string;
  restaurant_id: string;
  name: string;
  document: string | null;
  phone: string | null;
  email: string | null;
  is_active: boolean;
  chart_account_id: string | null;
}

export interface CreateSupplierInput {
  name: string;
  document?: string;
  phone?: string;
  email?: string;
  chart_account_id?: string;
}

export interface UpdateSupplierInput {
  name: string;
  document?: string;
  phone?: string;
  email?: string;
  chart_account_id?: string;
}

export function useSuppliers(options?: { includeInactive?: boolean }) {
  const { activeRestaurant } = useRestaurantContext();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const includeInactive = options?.includeInactive ?? false;

  const fetchSuppliers = useCallback(async () => {
    if (!activeRestaurant) {
      setSuppliers([]);
      return;
    }

    setIsLoading(true);
    try {
      let query = supabase
        .from("suppliers" as any)
        .select("id, restaurant_id, name, document, phone, email, is_active, chart_account_id")
        .eq("restaurant_id", activeRestaurant.id)
        .order("name");

      if (!includeInactive) {
        query = query.eq("is_active", true);
      }

      const { data, error } = await query;

      if (error) throw error;
      setSuppliers(((data as unknown) as Supplier[]) || []);
    } catch (e) {
      console.error("Error fetching suppliers", e);
      toast.error("Erro ao carregar fornecedores");
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant, includeInactive]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const createSupplier = useCallback(
    async (input: CreateSupplierInput) => {
      if (!activeRestaurant) return null;
      try {
        const { data, error } = await supabase
          .from("suppliers" as any)
          .insert({
            restaurant_id: activeRestaurant.id,
            name: input.name.trim(),
            document: input.document?.trim() || null,
            phone: input.phone?.trim() || null,
            email: input.email?.trim() || null,
            chart_account_id: input.chart_account_id || null,
            is_active: true,
          })
          .select("id")
          .single();

        if (error) throw error;
        toast.success("Fornecedor/credor cadastrado");
        await fetchSuppliers();
        return ((data as unknown) as { id?: string } | null)?.id || null;
      } catch (e) {
        console.error("Error creating supplier", e);
        toast.error("Erro ao cadastrar fornecedor/credor");
        return null;
      }
    },
    [activeRestaurant, fetchSuppliers]
  );

  const updateSupplier = useCallback(
    async (id: string, input: UpdateSupplierInput) => {
      if (!activeRestaurant) return false;
      try {
        const { error } = await supabase
          .from("suppliers" as any)
          .update({
            name: input.name.trim(),
            document: input.document?.trim() || null,
            phone: input.phone?.trim() || null,
            email: input.email?.trim() || null,
            chart_account_id: input.chart_account_id || null,
          })
          .eq("id", id)
          .eq("restaurant_id", activeRestaurant.id);

        if (error) throw error;
        toast.success("Fornecedor/credor atualizado");
        await fetchSuppliers();
        return true;
      } catch (e) {
        console.error("Error updating supplier", e);
        toast.error("Erro ao atualizar fornecedor/credor");
        return false;
      }
    },
    [activeRestaurant, fetchSuppliers]
  );

  const toggleSupplierActive = useCallback(
    async (id: string, isActive: boolean) => {
      if (!activeRestaurant) return false;
      try {
        const { error } = await supabase
          .from("suppliers" as any)
          .update({ is_active: isActive })
          .eq("id", id)
          .eq("restaurant_id", activeRestaurant.id);

        if (error) throw error;
        toast.success(isActive ? "Fornecedor reativado" : "Fornecedor desativado");
        await fetchSuppliers();
        return true;
      } catch (e) {
        console.error("Error toggling supplier active", e);
        toast.error("Erro ao alterar status do fornecedor");
        return false;
      }
    },
    [activeRestaurant, fetchSuppliers]
  );

  return {
    suppliers,
    isLoading,
    refetch: fetchSuppliers,
    createSupplier,
    updateSupplier,
    toggleSupplierActive,
  };
}
